import JsSIP from "jssip";

export const SIP_SETTING_KEYS = [
  "sipEnabled",
  "sipWebsocketUrl",
  "sipDomain",
  "sipUsername",
  "sipPassword",
  "sipDisplayName",
  "sipDialPrefix",
  "sipTargetDomain",
];

const SIP_DEFAULT_VALUES = {
  sipEnabled: "disabled",
  sipWebsocketUrl: "",
  sipDomain: "",
  sipUsername: "",
  sipPassword: "",
  sipDisplayName: "",
  sipDialPrefix: "",
  sipTargetDomain: "",
};

let sipUa = null;
let sipUaSignature = "";
let sipRegistrationPromise = null;
let activeSession = null;

const cleanString = value => (typeof value === "string" ? value.trim() : "");

const extractSettingsMap = settingsArray => {
  const values = { ...SIP_DEFAULT_VALUES };

  if (!Array.isArray(settingsArray)) {
    return values;
  }

  settingsArray.forEach(setting => {
    if (!setting?.key || !(setting.key in values)) {
      return;
    }

    values[setting.key] = typeof setting.value === "string" ? setting.value : "";
  });

  return values;
};

export const parseSipSettings = settingsArray => {
  const values = extractSettingsMap(settingsArray);

  const parsed = {
    enabled: values.sipEnabled === "enabled",
    websocketUrl: cleanString(values.sipWebsocketUrl),
    domain: cleanString(values.sipDomain),
    username: cleanString(values.sipUsername),
    password: cleanString(values.sipPassword),
    displayName: cleanString(values.sipDisplayName),
    dialPrefix: cleanString(values.sipDialPrefix),
    targetDomain: cleanString(values.sipTargetDomain),
    raw: values,
  };

  if (!parsed.displayName) {
    parsed.displayName = parsed.username;
  }

  if (!parsed.targetDomain) {
    parsed.targetDomain = parsed.domain;
  }

  const missingFields = [];

  if (!parsed.websocketUrl) missingFields.push("URL WebSocket");
  if (!parsed.domain) missingFields.push("Domínio SIP");
  if (!parsed.username) missingFields.push("Usuário/Ramal SIP");
  if (!parsed.password) missingFields.push("Senha SIP");

  parsed.missingFields = missingFields;
  parsed.isConfigured = missingFields.length === 0;

  return parsed;
};

export const sanitizePhoneNumber = number => {
  if (!number) {
    return "";
  }

  return String(number).replace(/\D/g, "");
};

export const buildDialedNumber = (number, dialPrefix = "") => {
  const sanitizedNumber = sanitizePhoneNumber(number);
  const sanitizedPrefix = sanitizePhoneNumber(dialPrefix);

  return `${sanitizedPrefix}${sanitizedNumber}`;
};

const resetSipClient = () => {
  sipRegistrationPromise = null;

  if (activeSession) {
    try {
      activeSession.terminate();
    } catch (error) {
      console.warn("Não foi possível finalizar a sessão SIP ativa:", error);
    }
    activeSession = null;
  }

  if (sipUa) {
    try {
      sipUa.stop();
    } catch (error) {
      console.warn("Não foi possível parar o cliente SIP:", error);
    }
  }

  sipUa = null;
  sipUaSignature = "";
};

const waitForRegistration = ua => {
  if (ua?.isRegistered?.()) {
    return Promise.resolve(ua);
  }

  if (sipRegistrationPromise) {
    return sipRegistrationPromise;
  }

  sipRegistrationPromise = new Promise((resolve, reject) => {
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      ua.off("registered", onRegistered);
      ua.off("registrationFailed", onRegistrationFailed);
      ua.off("disconnected", onDisconnected);
      ua.off("transportError", onTransportError);
      sipRegistrationPromise = null;
    };

    const onRegistered = () => {
      cleanup();
      resolve(ua);
    };

    const onRegistrationFailed = event => {
      cleanup();
      reject(new Error(event?.cause || "Falha ao registrar no servidor SIP."));
    };

    const onDisconnected = () => {
      cleanup();
      reject(new Error("Conexão WebSocket SIP desconectada."));
    };

    const onTransportError = event => {
      cleanup();
      reject(new Error(event?.cause || "Erro de transporte WebSocket SIP."));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Tempo esgotado ao registrar no servidor SIP."));
    }, 15000);

    ua.on("registered", onRegistered);
    ua.on("registrationFailed", onRegistrationFailed);
    ua.on("disconnected", onDisconnected);
    ua.on("transportError", onTransportError);
  });

  return sipRegistrationPromise;
};

const attachSessionListeners = ua => {
  ua.on("newRTCSession", event => {
    const session = event?.session;

    if (!session) {
      return;
    }

    activeSession = session;

    const clearActiveSession = () => {
      if (activeSession === session) {
        activeSession = null;
      }
    };

    session.on("ended", clearActiveSession);
    session.on("failed", clearActiveSession);
  });
};

const createOrReuseUa = async settings => {
  const signature = JSON.stringify({
    websocketUrl: settings.websocketUrl,
    domain: settings.domain,
    username: settings.username,
    password: settings.password,
    displayName: settings.displayName,
  });

  if (sipUa && sipUaSignature !== signature) {
    resetSipClient();
  }

  if (!sipUa) {
    const socket = new JsSIP.WebSocketInterface(settings.websocketUrl);

    sipUa = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${settings.username}@${settings.domain}`,
      password: settings.password,
      display_name: settings.displayName || settings.username,
      register: true,
      session_timers: false,
    });

    attachSessionListeners(sipUa);
    sipUa.start();
    sipUaSignature = signature;
  }

  return waitForRegistration(sipUa);
};

export const startSipCall = async (settings, phoneNumber) => {
  if (!settings?.enabled) {
    throw new Error("A telefonia SIP está desativada.");
  }

  if (!settings?.isConfigured) {
    throw new Error(
      `Configuração SIP incompleta: ${settings?.missingFields?.join(", ") || "verifique os campos obrigatórios."}`
    );
  }

  const dialedNumber = buildDialedNumber(phoneNumber, settings.dialPrefix);

  if (!dialedNumber) {
    throw new Error("O contato não possui um número válido para discagem.");
  }

  if (typeof navigator !== "undefined" && !navigator.mediaDevices?.getUserMedia) {
    throw new Error("O navegador não suporta áudio WebRTC para ligação SIP.");
  }

  const ua = await createOrReuseUa(settings);
  const target = `sip:${dialedNumber}@${settings.targetDomain || settings.domain}`;

  try {
    const session = ua.call(target, {
      mediaConstraints: {
        audio: true,
        video: false,
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: 1,
        offerToReceiveVideo: 0,
      },
      sessionTimersExpires: 120,
    });

    activeSession = session;

    session.on("failed", event => {
      console.error("Falha na sessão SIP:", event?.cause || event);
    });

    return {
      target,
      dialedNumber,
    };
  } catch (error) {
    throw new Error(error?.message || "Não foi possível iniciar a ligação SIP.");
  }
};

export const disconnectSipClient = () => {
  resetSipClient();
};
